import type { z } from 'zod';
import {
  ContractViolationError,
  NetworkError,
  describeZodIssues,
  isUnauthenticated,
  toApiRequestError,
} from './errors';

/**
 * The single HTTP boundary of the app.
 *
 * Invariants enforced here, so no screen has to remember them:
 *
 *  1. **Every** response body is validated with its zod schema before it is
 *     handed back. A mismatch throws `ContractViolationError`; nothing ever
 *     reaches a component as unvalidated `unknown`.
 *  2. The session is an httpOnly cookie. Nothing is read from or written to
 *     `localStorage`/`sessionStorage`, and no token is ever held in JS.
 *     `credentials: 'same-origin'` is what carries the session.
 *  3. Every state-changing request carries the CSRF header. A cross-site page
 *     cannot set a custom header without a preflight the API refuses.
 *  4. Money never becomes a `number`: bodies are serialised from the strings
 *     the caller already holds, and `JSON.parse` is left alone because every
 *     monetary field in the contract is typed as a string.
 */

export const BASE_URL = '/api';

/** Must match `apps/api/src/security/csrf.ts`. */
export const CSRF_HEADER_NAME = 'X-Requested-With';
export const CSRF_HEADER_VALUE = 'btcbot';

export const IDEMPOTENCY_HEADER_NAME = 'Idempotency-Key';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  readonly method?: HttpMethod;
  readonly query?: Readonly<Record<string, QueryValue>>;
  readonly body?: unknown;
  readonly idempotencyKey?: string;
  readonly signal?: AbortSignal;
}

type Listener = () => void;

const unauthenticatedListeners = new Set<Listener>();

/**
 * Subscribes to "the session is gone" — fired whenever any request comes back
 * `UNAUTHENTICATED`. The session provider uses this to drop straight to the
 * login screen instead of leaving stale numbers on the display.
 */
export function onUnauthenticated(listener: Listener): () => void {
  unauthenticatedListeners.add(listener);
  return () => {
    unauthenticatedListeners.delete(listener);
  };
}

function notifyUnauthenticated(): void {
  for (const listener of unauthenticatedListeners) listener();
}

export function buildUrl(path: string, query?: Readonly<Record<string, QueryValue>>): string {
  const base = `${BASE_URL}${path}`;
  if (query === undefined) return base;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue;
    params.set(key, String(value));
  }
  const serialised = params.toString();
  return serialised === '' ? base : `${base}?${serialised}`;
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.trim() === '') return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { __raw: text };
  }
}

/**
 * Performs a request and validates the response against `schema`.
 *
 * @throws {ApiRequestError} the server answered with its error envelope
 * @throws {ContractViolationError} 2xx body that does not match the contract
 * @throws {NetworkError} no HTTP response at all
 */
export async function apiRequest<S extends z.ZodTypeAny>(
  path: string,
  schema: S,
  options: RequestOptions = {},
): Promise<z.infer<S>> {
  const method = options.method ?? 'GET';
  const url = buildUrl(path, options.query);

  const headers: Record<string, string> = {
    Accept: 'application/json',
    [CSRF_HEADER_NAME]: CSRF_HEADER_VALUE,
  };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (options.idempotencyKey !== undefined) {
    headers[IDEMPOTENCY_HEADER_NAME] = options.idempotencyKey;
  }

  const init: RequestInit = {
    method,
    headers,
    credentials: 'same-origin',
    cache: 'no-store',
  };
  if (options.body !== undefined) init.body = JSON.stringify(options.body);
  if (options.signal !== undefined) init.signal = options.signal;

  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new NetworkError(`${method} ${url}`, cause);
  }

  const body = await readBody(response);

  if (!response.ok) {
    const error = toApiRequestError(response.status, body);
    if (isUnauthenticated(error)) notifyUnauthenticated();
    throw error;
  }

  const parsed = schema.safeParse(body);
  if (!parsed.success) {
    throw new ContractViolationError(`${method} ${url}`, describeZodIssues(parsed.error));
  }
  return parsed.data as z.infer<S>;
}
